import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBusinessCard } from 'components/BusinessCard/context'
import { COMPANY_TYPE, DEAL_TYPE, PERSON_TYPE, PROJECT_TYPE, PROJECT_MILESTONE_TYPE } from '../constants'

import Highlighter from 'components/BusinessCard/components/Highlighter'

import { Name, NameLink, SupportingText, HeaderTitlesContainer } from './StyledComponents'
import { formatDate, formatTime } from 'utils/date'

const Titles = () => {
  const { t } = useTranslation()
  const {
    cardMeta: {
      isPrimary,
      highlighter,
      linkifyName
    },
    callbacks: {
      onClick
    },
    resource,
    resourceType
  } = useBusinessCard()

  const name = useMemo(() => {
    const resourceName = resource.name || resource.full_name || `${resource.first_name} ${resource.last_name}`
    return `${resourceName}${resource.is_sample ? ` ${t('businessCard.sample')}` : ''}`
  }, [resource.name, resource.full_name, resource.first_name, resource.last_name, resource.is_sample, t])

  const url = useMemo(() => {
    switch (resourceType) {
      case COMPANY_TYPE:
        return `/companies/${resource.id}`
      case DEAL_TYPE:
        return `/deals/${resource.id}`
      case PROJECT_TYPE:
        return `/projects/${resource.id}`
      case PROJECT_MILESTONE_TYPE:
        return `/projects/${resource.project_id}`
      default:
        return `/people/${resource.id}`
    }
  }, [resource.id, resource.project_id, resourceType])

  const supportingText = useMemo(() => {
    if (resourceType === PERSON_TYPE) {
      return isPrimary ? t('businessCard.primaryContact') : resource.is_key_contact ? t('businessCard.keyContact') : resource.email || resource.home_email || resource.email2
    } else {
      return `${formatDate(resource.updated_at)}, ${formatTime(resource.updated_at)}`
    }
  }, [resourceType, resource, isPrimary, t])

  return (
    <HeaderTitlesContainer data-cypress="business-card-titles">
      {linkifyName ? (
        <NameLink
          href={url}
          onClick={onClick}
          title={name}
          withTextDecoration={false}
        >
          <Highlighter term={highlighter}>{name}</Highlighter>
        </NameLink>
      ) : (
        <Name>
          <Highlighter term={highlighter}>
            {name}
          </Highlighter>
        </Name>
      )}
      <SupportingText>
        {supportingText}
      </SupportingText>
    </HeaderTitlesContainer>
  )
}

export default Titles
